import React from "react";
import { Link } from "react-router-dom";
import LiveCover from "../common/LiveCover";
import "./ProjectCard.css";

/**
 * Compose the one-line metadata string shown under a project's title.
 *
 * Typology is always known; location and year are not, so they are joined
 * only when present rather than rendering an empty separator.
 *
 * @param {import("../../data/projects").Project} project
 * @returns {string}
 */
function formatProjectMetaLine(project) {
  return [project.typology, project.location, project.year]
    .filter(Boolean)
    .join(" · ");
}

/**
 * A single project, as a linked image with its caption set below a hairline.
 *
 * The card has no border, no shadow and no rounded corners: the photograph is
 * the object, and the rule under it is the only chrome. On hover the image
 * scales very slightly and the title shifts — enough to register as
 * interactive without animating the whole tile.
 *
 * Where the project has a `preview`, the photograph comes to life as the card
 * approaches the viewport — see `LiveCover`, which keeps the still underneath
 * so there is never a gap to look at.
 *
 * @param {Object} props
 * @param {import("../../data/projects").Project} props.project
 * @param {string} [props.ratioClass="frame--wide"] Aspect-ratio utility class.
 * @param {string} [props.ordinal]  Two-digit index shown with the title.
 * @param {"lazy"|"eager"} [props.loading="lazy"]
 * @param {string} [props.sizes]    Responsive sizes hint for the browser.
 * @returns {JSX.Element}
 */
function ProjectCard({
  project,
  ratioClass = "frame--wide",
  ordinal,
  loading = "lazy",
  sizes = "(min-width: 64rem) 50vw, 100vw",
}) {
  const metaLine = formatProjectMetaLine(project);

  return (
    <article className="project-card">
      <Link
        className="project-card__link"
        to={`/project/${project.slug}`}
        aria-label={`${project.name} — ${metaLine}`}
      >
        <div className={`frame frame--zoom ${ratioClass}`} data-reveal="wipe">
          <LiveCover
            coverSrc={project.cover}
            alt={`${project.name} — arhitektonska vizualizacija`}
            preview={project.preview}
            loading={loading}
            sizes={sizes}
          />

          {project.hasVR && (
            <span className="project-card__tag tag">
              <span className="project-card__tag-dot" aria-hidden="true" />
              VR šetnja
            </span>
          )}
        </div>

        <div className="project-card__caption">
          <h3 className="project-card__title">
            {ordinal && (
              <span className="project-card__ordinal t-numeral">{ordinal}</span>
            )}
            <span className="project-card__name">{project.name}</span>
          </h3>
          <p className="project-card__meta t-meta">{metaLine}</p>
        </div>
      </Link>
    </article>
  );
}

export default ProjectCard;
